import { PropsWithChildren, createContext, useContext, useMemo, useReducer } from 'react';

interface PlaylistState {
  /** Active Index starting from 0 to totalItems - 1, the index for the scroll child that's hitting the left edge of the scroll container */
  activeIndex: number;
  /** Items per view, aka the item counts for each page */
  pageSize: number;
  /** The total counts of items */
  totalItems: number;
  /** Whether currently in the first page, aka no prev page */
  isFirstPage: boolean;
  /** Whether currently in the last page, aka no next page */
  isLastPage: boolean;
  /** Whether should scroll the current active index into view */
  isScrolling?: boolean;
}

export interface PlaylistStyles {
  /** Default to be 400(32px), spacing for all playlist partials; use Bokeh space tokens */
  outerSpacing?: number | string;
  /** Default to false, fullbleed carousel will has NO inner padding on x-axis */
  isFullbleed?: boolean;
  /** Default to be 300(24px), gap between each list child, and gap between playlistHeader and body list */
  gap?: number | string;
}

type PlaylistAction =
  | {
      type: 'STATE_UPDATE';
      payload: Partial<PlaylistState>;
    }
  | {
      type: 'NEXT_PAGE' | 'PREV_PAGE';
    }
  | {
      type: 'ACTIVE_ITEM_CHANGE';
      payload: { activeIndex: number; isScrolling?: boolean };
    }
  | {
      type: 'SCROLLED_INTO_VIEW';
    };

interface PlaylistContext {
  state: PlaylistState;
  dispatch: React.Dispatch<PlaylistAction>;
  styles: PlaylistStyles;
}

const reducer = (state: PlaylistState, action: PlaylistAction) => {
  switch (action.type) {
    case 'STATE_UPDATE': {
      return {
        ...state,
        ...action.payload,
      };
    }

    case 'NEXT_PAGE': {
      const { activeIndex, pageSize, totalItems } = state;

      // When no children or context not ready, pagination should not work
      if (totalItems === 0) return state;

      // Something is wrong if totalItems is less than pageSize, scroll to the first item
      // Fail-safe, should never happen in real world
      if (totalItems < pageSize)
        return {
          ...state,
          activeIndex: 0,
          isFirstPage: true,
          isLastPage: false,
        };

      // IMPORTANT: Since we don't fill the last page with empty items or space, it's impossible to scroll the last item to the left edge
      // Scroll end is when last item is fully visible
      // e.g if totalItems === 7, pageSize === 3, page looks like this [0], [1,2,3], last page [4,5,6]; 4 is the item that's fully visible and closest left edge
      // So we reach last page if the new activeIndex reach or go beyond the first item in last page
      // Edge case: when scroll container is very large, total items is the same as page size, endItemIndex is 0, any active index change will be on the last page, match this check
      const endItemIndex = totalItems - pageSize;
      const isLastPage = activeIndex + pageSize >= endItemIndex;

      return {
        ...state,
        isFirstPage: false,
        isLastPage,
        activeIndex: isLastPage ? endItemIndex : activeIndex + pageSize,
        isScrolling: true,
      };
    }

    case 'PREV_PAGE': {
      const { activeIndex, pageSize } = state;

      // Back to first page if the new activeIndex gonna reach or go below the first item in the list
      const isFirstPage = activeIndex - pageSize <= 0;

      return {
        ...state,
        isFirstPage,
        isLastPage: false,
        activeIndex: isFirstPage ? 0 : activeIndex - pageSize,
        isScrolling: true,
      };
    }

    case 'ACTIVE_ITEM_CHANGE': {
      const { totalItems, pageSize } = state;
      const { activeIndex, isScrolling = false } = action.payload;

      const endItemIndex = totalItems - pageSize;
      const isLastPage = activeIndex >= endItemIndex;

      return {
        ...state,
        activeIndex: isLastPage ? endItemIndex : activeIndex,
        isFirstPage: activeIndex === 0,
        isLastPage,
        isScrolling,
      };
    }

    case 'SCROLLED_INTO_VIEW': {
      return {
        ...state,
        isScrolling: false,
      };
    }

    default:
      return state;
  }
};

const initialState: PlaylistState = {
  activeIndex: 0,
  pageSize: 1,
  totalItems: 0,
  isFirstPage: true,
  isLastPage: false,
};

const initialStyles: PlaylistStyles = {
  outerSpacing: 400,
  gap: 300,
  isFullbleed: false,
};

export const PlaylistContext = createContext<PlaylistContext>({
  styles: initialStyles,
  state: initialState,
  dispatch: () => {
    throw new Error('Not implemented');
  },
});

export const PlaylistProvider = ({ children, ...rest }: PropsWithChildren<PlaylistStyles>) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Deep clone "rest", but take out entries with undefined value
  const value = useMemo(() => {
    return { styles: { ...initialStyles, ...JSON.parse(JSON.stringify(rest)) }, state, dispatch };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return <PlaylistContext.Provider value={value}>{children}</PlaylistContext.Provider>;
};

export const usePlaylist = () => {
  const context = useContext(PlaylistContext);

  if (!context) {
    throw new Error('usePlaylist must be used within a PlaylistProvider');
  }

  return context;
};

export const usePlaylistStyles = () => {
  const context = useContext(PlaylistContext);

  if (!context) {
    throw new Error('usePlaylist must be used within a PlaylistProvider');
  }

  return context.styles;
};
